import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useWallet } from '@/contexts/WalletContext';
import styles from '@/styles/components/bridge/BridgePanel.module.css';
import { bridgeService } from '@/services/bridgeService';
import { BRIDGE_TOKENS } from '@/constants';
import { getWalletTokenBalance, getNativeBalance } from '@/services/tokenBalanceService';
import BridgeModal from './BridgeModal';

type TokenSymbol = 'SWM' | 'WETH';

interface TokenOption {
  symbol: TokenSymbol;
  label: string;
}

const TOKENS: TokenOption[] = [
  { symbol: 'SWM', label: 'SWM' },
  { symbol: 'WETH', label: 'WETH' },
];

const TOKEN_TO_CHAIN: Record<TokenSymbol, string> = {
  SWM: 'Base',
  WETH: 'Swarm',
};

const TOKEN_ICON_SRC: Record<TokenSymbol, string> = {
  SWM: '/icons/tokens/swm.svg',
  WETH: '/icons/tokens/weth.svg',
};

const iconCache: Record<TokenSymbol, boolean> = {
  SWM: false,
  WETH: false,
};

const Avatar: React.FC<{ symbol: TokenSymbol }> = ({ symbol }) => {
  const [imgOk, setImgOk] = useState<boolean>(() => iconCache[symbol]);
  const src = TOKEN_ICON_SRC[symbol];

  useEffect(() => {
    if (iconCache[symbol]) {
      setImgOk(true);
      return undefined;
    }

    const loader = new Image();
    loader.src = src;
    loader.onload = () => {
      iconCache[symbol] = true;
      setImgOk(true);
    };
    loader.onerror = () => setImgOk(false);

    return () => {
      loader.onload = null;
      loader.onerror = null;
    };
  }, [symbol, src]);

  return imgOk ? (
    <img
      src={src}
      alt={`${symbol} icon`}
      width={28}
      height={28}
      onError={() => setImgOk(false)}
      className={styles.avatarImg}
    />
  ) : (
    <div className={styles.avatarFallback}>{symbol[0]}</div>
  );
};

const useOutsideClick = (
  ref: React.RefObject<HTMLElement>,
  onClose: () => void
) => {
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClose]);
};

interface TokenSelectorProps {
  value: TokenSymbol;
  onChange: (val: TokenSymbol) => void;
}

const TokenSelector: React.FC<TokenSelectorProps> = React.memo(
  ({ value, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    useOutsideClick(ref, () => setOpen(false));

    return (
      <div className={styles.tokenSelectorWrapper} ref={ref}>
        <div className={styles.tokenPill} onClick={() => setOpen(v => !v)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar symbol={value} />
            <span className={styles.tokenLabel}>{value}</span>
          </div>
          <span className={styles.caret}>▾</span>
        </div>
        <div
          className={`${styles.dropdown} ${
            open ? styles.dropdownVisible : styles.dropdownHidden
          }`}
        >
          {TOKENS.map(t => (
            <div
              key={t.symbol}
              onClick={() => {
                onChange(t.symbol);
                setOpen(false);
              }}
              className={`${styles.dropdownItem} ${
                t.symbol === value ? styles.dropdownItemActive : ''
              }`}
            >
              <Avatar symbol={t.symbol} />
              <div style={{ fontWeight: 600 }}>{t.label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }
);

interface BridgePanelProps {
  style?: React.CSSProperties;
}

const BridgePanel: React.FC<BridgePanelProps> = ({ style }) => {
  const { state } = useWallet();

  const [fromToken, setFromToken] = useState<TokenSymbol>('WETH');
  const [toToken, setToToken] = useState<TokenSymbol>('SWM');
  const [fromAmount, setFromAmount] = useState<string>('');
  const [toAmount, setToAmount] = useState<string>('');
  const [isBridging, setIsBridging] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState<string>('');
  const [txHash, setTxHash] = useState<string>('');
  const [fromBalance, setFromBalance] = useState<string>('0.00');
  const [toBalance, setToBalance] = useState<string>('—');

  const getFromBalanceText = () => fromBalance;
  const getToBalanceText = () => toBalance;

  const chainId = useMemo(() => {
    const id = state?.network?.chainId as string | number | undefined;
    if (!id) return 0;
    if (typeof id === 'number') return id;
    const s = String(id);
    return s.startsWith('0x') ? parseInt(s, 16) : parseInt(s, 10);
  }, [state?.network?.chainId]);

  const isBase = chainId === 8453;
  const isCustom = chainId === 1322837 || chainId === 0x142f55;

  

  // Quote update
  useEffect(() => {
    let mounted = true;
    (async () => {
      setError('');
      setTxHash('');
      const amount = fromAmount && Number(fromAmount) > 0 ? fromAmount : '';
      if (!amount) {
        setToAmount('');
        return;
      }
      try {
        // Use current network as source; other chain as destination
        const sourceChain = isCustom ? 1322837 : 8453;
        const targetChain = sourceChain === 1322837 ? 8453 : 1322837;
        const quote = await bridgeService.getQuote(amount, sourceChain, targetChain, fromToken as any);
        if (mounted) setToAmount(quote.amountOutEstimated);
      } catch (e: any) {
        if (mounted) setToAmount('');
      }
    })();
    return () => { mounted = false; };
  }, [fromAmount, fromToken]);

  // Balance update for FROM card on current network
  useEffect(() => {
    let mounted = true;
    (async () => {
      const addr = state?.address;
      if (!addr) { setFromBalance('0.00'); return; }
      let tokenAddress: string | undefined;
      let decimals = 18;
      if (isBase) {
        if (fromToken === 'SWM') { tokenAddress = BRIDGE_TOKENS.BASE.SWM; decimals = 9; }
      } else if (isCustom) {
        if (fromToken === 'WETH') {
          // Show native ETH on Swarm for convenience
          const bal = await getNativeBalance(addr);
          if (mounted) setFromBalance(bal);
          return;
        }
        if (fromToken === 'SWM') { tokenAddress = BRIDGE_TOKENS.CUSTOM.SWM; decimals = 9; }
      }
      if (!tokenAddress) { setFromBalance('0.00'); return; }
      const bal = await getWalletTokenBalance(tokenAddress, decimals, addr);
      if (mounted) setFromBalance(bal);
    })();
    return () => { mounted = false; };
  }, [state?.address, isBase, isCustom, fromToken]);

  // Balance update for TO card 
  useEffect(() => {
    let mounted = true;
    (async () => {
      const addr = state?.address;
      if (!addr) { setToBalance('—'); return; }
      try {
        if (isCustom) {
          if (toToken === 'WETH') {
            const bal = await getNativeBalance(addr);
            if (mounted) setToBalance(bal);
            return;
          }
          if (toToken === 'SWM') {
            const bal = await getWalletTokenBalance(BRIDGE_TOKENS.CUSTOM.SWM, 9, addr);
            if (mounted) setToBalance(bal);
            return;
          }
        } else if (isBase) {
          if (toToken === 'SWM') {
            const bal = await getWalletTokenBalance(BRIDGE_TOKENS.BASE.SWM, 9, addr);
            if (mounted) setToBalance(bal);
            return;
          }
          if (toToken === 'WETH') {
            // WETH not on Base; we don't switch chains here
            if (mounted) setToBalance('—');
            return;
          }
        }
        if (mounted) setToBalance('—');
      } catch {
        if (mounted) setToBalance('—');
      }
    })();
    return () => { mounted = false; };
  }, [state?.address, isCustom, isBase, toToken]);

  const canConfirm = useMemo(() => {
    if (!fromAmount || Number(fromAmount) <= 0) return false;
    return true;
  }, [fromAmount]);

  const handleConfirm = async () => {
    if (!canConfirm || isBridging) return;
    setError('');
    setTxHash('');
    setShowModal(true);
  };

  const handleSwap = () => {
    setFromToken(toToken);
    setToToken(fromToken);
    setFromAmount(toAmount);
    setToAmount(fromAmount);
  };



  return (
    <div className={styles.container} style={style}>
      <div className={styles.card}>
        <div className={styles.label}>From</div>
        <div className={styles.chainBadge}>
          <span style={{ fontSize: 12, color: '#5B616D' }}>🌐</span>
          <span style={{ color: '#5B616D' }}>{TOKEN_TO_CHAIN[fromToken]}</span>
        </div>
        <div className={styles.row}>
          <input
            inputMode="decimal"
            placeholder="0.00"
            value={fromAmount}
            onChange={e => {
              const raw = e.target.value.replace(/[^0-9.]/g, '');
              const parts = raw.split('.');
              const sanitized = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : raw;
              setFromAmount(sanitized);
            }}
            className={styles.amountInput}
          />
          <TokenSelector value={fromToken} onChange={setFromToken} />
        </div>
        <div className={styles.subText}>Balance: {getFromBalanceText()}</div>
      </div>

      <div className={styles.swapButton} onClick={handleSwap} aria-label="Swap tokens">
        <span className={styles.swapIcon}>⇅</span>
      </div>

      <div className={styles.card}>
        <div className={styles.label}>To</div>
        <div className={styles.chainBadge}>
          <span style={{ fontSize: 12, color: '#5B616D' }}>🌐</span>
          <span style={{ color: '#5B616D' }}>{TOKEN_TO_CHAIN[toToken]}</span>
        </div>
        <div className={styles.row}>
          <input
            inputMode="decimal"
            placeholder="0.00"
            value={toAmount}
            readOnly
            className={styles.amountInput}
          />
          <TokenSelector value={toToken} onChange={setToToken} />
        </div>
        <div className={styles.subText}>Balance: {getToBalanceText()}</div>
      </div>

      <button type="button" className={styles.confirmButton} disabled={!canConfirm || isBridging} onClick={handleConfirm}>
        {isBridging ? 'Processing…' : 'Confirm'}
      </button>
      {showModal && (
        <BridgeModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          token={fromToken as any}
          amount={fromAmount}
          requiredChain={isCustom ? 'CUSTOM' : 'BASE'}
        />
      )}
      <div style={{ fontSize: 12, paddingTop: '10px', paddingLeft: '2px', color: 'var(--text-low_em, #8D91A0)' }}>
        Network: {isBase ? 'Base' : isCustom ? 'Swarm' : 'Unsupported network'}
      </div>
    </div>
  );
};

export default BridgePanel;


